import {
  Accordion,
  AccordionSummary,
  FormControlLabel,
  Checkbox,
  AccordionDetails,
  Button,
  Badge,
} from "@mui/material";
import Flex from "./Flex";
import OutlineCard from "./OutlineCard";
import { ScrollArea } from "./ScrollBar";
import Text from "./Text";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SquareIcon from "@mui/icons-material/Square";
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_SECONDARY_TEXT_COLOR,
} from "../utils/utils";

type ListOption = {
  label: string;
  selected: boolean;
  color?: string;
};

interface DataValues extends Object {
  optList: ListOption[];
  isExpand: boolean;
  isSelectAll: boolean;
  activeFilterCount: number;
}

interface AccordionFiltersProps<T> {
  data: T;
  onSelectAll?: (k: keyof T, checked: boolean) => void;
  onChange: (k: keyof T, stat: boolean, ind: number) => void;
  onExpand: (k: keyof T, isExpand: boolean) => void;
  onClear?: (k: keyof T) => void;
}

const AccordionFilters = <T extends Record<keyof T, DataValues>>({
  data,
  onClear,
  onSelectAll,
  onChange,
  onExpand,
}: AccordionFiltersProps<T>) => {
  return Object.entries(data)?.map(([k, val]) => {
    const v = val as DataValues;

    return (
      <Accordion
        key={k}
        expanded={v?.isExpand}
        onChange={(_, isExpand) => onExpand(k as keyof T, isExpand)}
        disableGutters
        elevation={0}
        sx={{
          border: `1px solid ${DEFAULT_BORDER_COLOR}`,
          borderRadius: "8px !important",
          marginBottom: "12px",
          backgroundColor: "transparent",
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            marginBottom: "12px",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />}
          sx={{
            minHeight: "34px",
            padding: "0 10px",
            backgroundColor: "transparent",
            "&.Mui-expanded": {
              minHeight: "34px",
              borderBottom: "1px solid #E2E8F0",
              backgroundColor: "transparent",
            },
            "& .MuiAccordionSummary-content": {
              margin: "0",
            },
          }}
        >
          <Flex
            width={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
            pr={1}
          >
            <Text
              fontWeight={"medium"}
              sx={{ color: DEFAULT_SECONDARY_TEXT_COLOR }}
            >
              {k}
            </Text>
            <Badge
              badgeContent={v.activeFilterCount}
              color={"info"}
              sx={{
                "& .MuiBadge-badge": { mr: 0.5, fontSize: "11px" },
              }}
            />
          </Flex>
        </AccordionSummary>

        <ScrollArea maxHeight="10rem" height={"inherited"}>
          <OutlineCard
            sx={{ border: "none", boxShadow: "none", padding: "8px 12px" }}
          >
            <Flex flexDirection={"column"}>
              {/* Select All option */}
              <FormControlLabel
                key={"all"}
                label={
                  <Text sx={{ color: DEFAULT_SECONDARY_TEXT_COLOR }}>All</Text>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={v.isSelectAll}
                    onChange={(e) =>
                      onSelectAll?.(k as keyof T, e.target.checked)
                    }
                  />
                }
              />

              {/* Other options */}
              {v.optList?.map((opt: ListOption, ind: number) => (
                <AccordionDetails
                  key={opt.label}
                  sx={{ margin: 0, padding: 0 }}
                >
                  <FormControlLabel
                    key={opt.label}
                    label={
                      <Flex gap={1} alignItems={"center"}>
                        {opt?.color && (
                          <SquareIcon
                            sx={{
                              borderRadius: 0.8,
                              height: "17px",
                              width: "17px",
                              backgroundColor: opt.color,
                              color: opt.color,
                              marginRight: "5px",
                            }}
                          />
                        )}
                        <Text sx={{ color: DEFAULT_SECONDARY_TEXT_COLOR }}>
                          {opt.label}
                        </Text>
                      </Flex>
                    }
                    control={
                      <Flex>
                        <Checkbox
                          size="small"
                          checked={opt.selected}
                          onChange={(e) =>
                            onChange(k as keyof T, e.target.checked, ind)
                          }
                        />
                      </Flex>
                    }
                  />
                </AccordionDetails>
              ))}
            </Flex>
          </OutlineCard>
        </ScrollArea>

        <Flex width={"100%"} justifyContent={"end"} sx={{ padding: "4px 8px" }}>
          <Button
            variant="text"
            size="small"
            sx={{
              fontSize: "13px",
              color: "#004AB1",
              textTransform: "none",
            }}
            onClick={() => onClear?.(k as keyof T)}
          >
            Clear
          </Button>
        </Flex>
      </Accordion>
    );
  });
};

export default AccordionFilters;
